import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, ShieldAlert, CheckCircle2 } from "lucide-react";
import { API } from "../lib/api";

const LOGO_COLOR =
  "/assets/logo-fped-color.svg";

const STATES = {
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
};

// The backend returns a single generic 401 for every failure mode (no enumeration).
// We surface a friendly Romanian message; if the URL came in with `reason=...`
// we show a more specific copy. The token-side check is authoritative.
const REASON_COPY = {
  expired: {
    title: "Link expirat",
    body: "Linkul de acces este valabil 15 minute și a expirat. Cere un link nou de la owner sau încearcă din nou de la pagina de login.",
  },
  used: {
    title: "Link deja folosit",
    body: "Acest link a fost folosit deja o singură dată. Din motive de securitate, fiecare link funcționează o singură dată — cere un link nou.",
  },
  invalid: {
    title: "Link invalid",
    body: "Linkul de acces este invalid sau a fost revocat. Cere un link nou.",
  },
  disabled: {
    title: "Cont dezactivat",
    body: "Contul tău a fost dezactivat. Contactează un owner pentru a-l reactiva.",
  },
  default: {
    title: "Link de acces invalid sau expirat",
    body: "Linkul nu mai poate fi folosit. Cere un link nou de la pagina de login.",
  },
};

export default function AdminMagic() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState(STATES.LOADING);
  const [errorKey, setErrorKey] = useState("default");
  const consumedRef = useRef(false);

  useEffect(() => {
    if (consumedRef.current) return; // StrictMode guard: prevent double-consume
    consumedRef.current = true;

    const token = params.get("token");
    if (!token) {
      setErrorKey("invalid");
      setState(STATES.ERROR);
      return;
    }

    // Browser-driven exchange. The backend returns a 303 redirect (Set-Cookie + Location).
    // We don't follow the redirect via fetch — instead we trigger a window.location swap
    // ourselves AFTER confirming the cookie was set. Doing it server-side via redirect
    // would also work but breaks if the SPA needs to show a transition UI first.
    fetch(`${API}/auth/magic/consume?token=${encodeURIComponent(token)}`, {
      method: "GET",
      credentials: "include",
      redirect: "manual", // don't auto-follow; we want to read the status
    })
      .then((res) => {
        // `redirect: manual` makes successful redirects appear as opaqueredirect (status 0).
        // That's our success signal — the cookie has been set.
        if (res.type === "opaqueredirect" || (res.status >= 300 && res.status < 400)) {
          setState(STATES.SUCCESS);
          // Tiny visual delay so the operator sees the confirmation before the redirect.
          setTimeout(() => navigate("/admin", { replace: true }), 700);
          return;
        }
        if (res.status === 401) {
          // Generic auth failure per anti-enumeration policy.
          setErrorKey("default");
          setState(STATES.ERROR);
          return;
        }
        setErrorKey("default");
        setState(STATES.ERROR);
      })
      .catch(() => {
        setErrorKey("default");
        setState(STATES.ERROR);
      });
  }, [params, navigate]);

  return (
    <div
      className="min-h-screen bg-[#F5F6F6] grid place-items-center px-4 sm:px-6 py-10"
      data-testid="admin-magic-page"
    >
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl border border-[#E5E7EB] px-7 sm:px-12 py-10 sm:py-14 shadow-xl flex flex-col items-center text-center">
          <img
            src={LOGO_COLOR}
            alt="Fundația pentru Educație Digitală"
            className="h-12 sm:h-14 w-auto"
          />

          {state === STATES.LOADING && (
            <>
              <div className="mt-8 h-12 w-12 rounded-2xl bg-[#965EA7] grid place-items-center text-white">
                <Loader2 size={20} className="animate-spin" />
              </div>
              <h1 className="mt-6 font-display text-2xl font-bold text-[#373A45]" data-testid="admin-magic-loading">
                Te autentificăm...
              </h1>
              <p className="mt-2 text-sm text-[#6B7280] max-w-xs">
                Verificăm linkul de acces. Durează câteva secunde.
              </p>
            </>
          )}

          {state === STATES.SUCCESS && (
            <>
              <div className="mt-8 h-12 w-12 rounded-2xl bg-[#519872] grid place-items-center text-white">
                <CheckCircle2 size={20} />
              </div>
              <h1 className="mt-6 font-display text-2xl font-bold text-[#373A45]" data-testid="admin-magic-success">
                Autentificare reușită
              </h1>
              <p className="mt-2 text-sm text-[#6B7280] max-w-xs">
                Te redirecționăm către dashboard...
              </p>
            </>
          )}

          {state === STATES.ERROR && (
            <>
              <div className="mt-8 h-12 w-12 rounded-2xl bg-[#9b1f2c] grid place-items-center text-white">
                <ShieldAlert size={20} />
              </div>
              <h1 className="mt-6 font-display text-2xl font-bold text-[#373A45]" data-testid="admin-magic-error">
                {REASON_COPY[errorKey].title}
              </h1>
              <p className="mt-2 text-sm text-[#6B7280] max-w-sm">
                {REASON_COPY[errorKey].body}
              </p>
              <Link
                to="/admin/login"
                className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold py-3 transition-all"
                data-testid="admin-magic-retry"
              >
                Înapoi la login
              </Link>
            </>
          )}
        </div>

        <div className="mt-6 flex justify-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#965EA7] rounded-full px-3 py-1.5 transition-colors"
          >
            <ArrowLeft size={14} />
            Pagina principală
          </Link>
        </div>
      </div>
    </div>
  );
}
